import { useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { createWheelQuoteApi, getRoundedValue } from "features/car_model/api/carModelApi";
import { EstWheelCreateRequest, NewQuoteMaker } from "features/car_model/types";
import { CostSettingInfo, WheelTireCombination } from "features/mst_proc_fee/types/proc-fee";
import Notify from "config/notify";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import { parseNumber } from "util/helper";
import dayjs from "dayjs";
import { DASH_FORMAT_DATE } from "constants/format";
import { shouldApplyCashbackToWheelQuote } from "features/mst_proc_fee/utils/priceCalculator";

interface CustomerInfo {
  customer_name: string;
  customer_tel_no: string;
  quote_by: string;
  exp_date: string;
}

interface SelectOptions {
  nat: Array<{ label: string; value: string }>;
  lockNat: Array<{ label: string; value: string }>;
  hub: Array<{ label: string; value: string }>;
  wcoating: Array<{ label: string; value: string }>;
}

interface UseCreateWheelQuoteParams {
  customerInfo: CustomerInfo;
  costSetting: CostSettingInfo;
  wheelTireCombinations: WheelTireCombination[];
  newQuoteMaker?: NewQuoteMaker;
  selectOptions: SelectOptions;
  wheelOptionsPrice: number;
}

/**
 * Helper: Get option item code
 * @param options - Array of options
 * @param selectedValue - Selected value string
 * @returns Option item code or empty string
 */
const getOptionItemCode = (options: any[], selectedValue: string): string => {
  if (!selectedValue || selectedValue === "なし") return "";
  const option = options.find((opt) => opt.value === selectedValue);
  return option?.value || "";
};

/**
 * cb_tire_price / cb_wheel_price for create API: actual amounts only if checkbox + 期間中; else both 0.
 */
const getCashbackPricesForWheelQuoteCreate = (
  costSetting: Pick<CostSettingInfo, "cashback_flag" | "cashback_period">,
  tire: { cb_tire_price?: number; cb_wheel_price?: number },
): { cb_tire_price: number; cb_wheel_price: number } => {
  if (!shouldApplyCashbackToWheelQuote(costSetting)) {
    return { cb_tire_price: 0, cb_wheel_price: 0 };
  }
  return {
    cb_tire_price: tire.cb_tire_price || 0,
    cb_wheel_price: tire.cb_wheel_price || 0,
  };
};

/**
 * Custom hook for creating wheel quote
 * @param params - Hook parameters
 * @param params.customerInfo - Customer information
 * @param params.costSetting - Cost settings
 * @param params.wheelTireCombinations - Wheel-tire combinations
 * @param params.newQuoteMaker - New quote maker data
 * @param params.selectOptions - Select options for autocomplete
 * @param params.wheelOptionsPrice - Total wheel options price
 * @returns Hook state and handlers
 */
export const useCreateWheelQuote = ({
  customerInfo,
  costSetting,
  wheelTireCombinations,
  newQuoteMaker,
  selectOptions,
  wheelOptionsPrice,
}: UseCreateWheelQuoteParams) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Handle create wheel quote
   * Note: Validation should be done before calling this function
   */
  const handleCreateWheelQuote = useCallback(async () => {
    setIsSubmitting(true);

    try {
      // Build request data
      // Note: newQuoteMaker should be validated before calling this function
      const requestData: EstWheelCreateRequest = {
        mode: "wheel",
        customer_name: customerInfo.customer_name,
        customer_tel_no: customerInfo.customer_tel_no,
        quote_by: customerInfo.quote_by,
        exp_date: dayjs(customerInfo.exp_date).format(DASH_FORMAT_DATE),
        brnd_label: newQuoteMaker?.brnd_label || "",
        brnd_name: newQuoteMaker?.brnd_name || "",
        modl_code: newQuoteMaker?.modl_code || 0,
        modl_name: newQuoteMaker?.modl_name || "",
        fmc_code: newQuoteMaker?.car_fmc || "",
        modl_period_s: newQuoteMaker?.modl_period_s || "",
        modl_period_e: newQuoteMaker?.modl_period_e || "",
        car_tiresize_id: newQuoteMaker?.selectedTire?.mmm_idx || 0,
        mmm_idx: newQuoteMaker?.selectedTire?.mmm_idx || null,
        tire_puncture_flag: costSetting.tire_puncture_flag,
        waste_tire_cost_flag: costSetting.waste_tire_cost_flag ? 1 : 0,
        rubber_stem_valve_flag: 0,
        nitrogen_inflation_flag: costSetting.nitrogen_inflation_flag ? 1 : 0,
        hub_rust_prevention_flag: costSetting.hub_rust_prevention_flag ? 1 : 0,
        replac_labor_cost: parseNumber(costSetting.replac_labor_cost),
        waste_tire_cost: parseNumber(costSetting.waste_tire_cost),
        rubber_stem_valve_cost: 0,
        nitrogen_inflation_cost: parseNumber(costSetting.nitrogen_inflation_cost),
        hub_rust_prevention_cost: parseNumber(costSetting.hub_rust_prevention_cost),
        nut_item_cd: getOptionItemCode(selectOptions.nat, costSetting.nut),
        lock_nut_item_cd: getOptionItemCode(selectOptions.lockNat, costSetting.lock_nut),
        hub_ring_item_cd: getOptionItemCode(selectOptions.hub, costSetting.hub_ring),
        wcoating_item_cd: getOptionItemCode(selectOptions.wcoating, costSetting.wheel_coating),
        option_total_price: wheelOptionsPrice,
        round_type: String(costSetting.rounding),
        take_out_flag: costSetting.take_out_flag ? 0 : 1,
        tbl_quote_detail_wheel: wheelTireCombinations
          .filter((combination) => combination.tires.some((tire) => tire.checked))
          .map((combination) => ({
            wheel_item_cd: combination.wheel.item_cd,
            image_url: combination.wheel.wheel_image_url || "",
            maker_name: combination.wheel.maker_name || "",
            item_name: combination.wheel.item_name || "",
            brand_name: combination.wheel.brand_name || "",
            wheel_size: combination.wheel.wheel_size || "",
            wheel_color_name: combination.wheel.wheel_color_name || "",
            quantity: 4,
            sale_price: combination.wheel.regular_price || 0,
            fixed_discount: combination.wheel.fixed_discount || 0,
            special_discount: 0, // Not available in MstItemDetailWheelDetail
            discounted_sale_price: combination.wheel.discounted_price || 0,
            remarks: combination.wheel.remarks || "",
            confirm_total_price: combination.wheel.final_price || 0,
            tbl_quote_detail_tire: combination.tires
              .filter((tire) => tire.checked)
              .map((tire) => {
                const { cb_tire_price, cb_wheel_price } = getCashbackPricesForWheelQuoteCreate(
                  costSetting,
                  tire,
                );
                return {
                  tire_item_cd: tire.tire_item_cd,
                  maker_name: tire.maker_name,
                  item_name: tire.item_name,
                  tire_loin_speed_mark: tire.tire_loin_speed_mark,
                  tire_label: tire.tire_label,
                  quantity: tire.quantity,
                  nomal_price: tire.nomal_price,
                  fixed_discount: tire.fixed_discount || 0,
                  special_discount: tire.special_discount || 0,
                  discounted_sale_price: tire.discounted_sale_price,
                  wage_cost_price: tire.wage_cost_price,
                  wage_cost_in_price: tire.wage_cost_in_price,
                  puncture_protection_price: tire.puncture_protection_price,
                  puncture_protection_in_price: tire.puncture_protection_in_price,
                  cb_tire_price,
                  cb_wheel_price,
                  wheel_item_cd: combination.wheel.item_cd,
                  confirm_total_price: getRoundedValue(
                    tire.confirm_total_price + combination.wheel.final_price,
                    Number(costSetting.rounding),
                  ),
                  remarks: tire.remarks || "",
                };
              }),
          })),
      };

      const response = await createWheelQuoteApi(requestData);

      if (response?.quote_id) {
        // Navigate to detail page with quote_id
        sessionStorage.removeItem(SESSION_KEY.WHEEL_STORE_STATE);
        sessionStorage.removeItem(SESSION_KEY.SELECTED_WHEEL);
        sessionStorage.removeItem(SESSION_KEY.SELECTED_TIRE);
        navigate(routes.quotationDetail.replace(":quotationId", response.quote_id));
      } else {
        Notify.setNotification({
          show: true,
          title: "見積の作成に失敗しました",
          actionsPosition: "center",
        });
      }
    } catch (error) {
      Notify.setNotification({
        show: true,
        title: "見積の作成に失敗しました",
        actionsPosition: "center",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    customerInfo,
    costSetting,
    wheelTireCombinations,
    newQuoteMaker,
    selectOptions,
    wheelOptionsPrice,
    navigate,
  ]);

  return {
    isSubmitting,
    handleCreateWheelQuote,
  };
};

export default useCreateWheelQuote;
